/**
 * trackers.ts — the vehicle's paired trackers, with the identifiers a human reads.
 *
 * `get_preparation_status` EMITS ONLY THE FOREIGN KEY. Its `tracker` details are
 * `{ id, type, tracker_id }`, and `tracker_id` is the `gps_trackers` row id — the "95" and "96"
 * the Vehicle area printed at an admin who has never seen a tracker's primary key and never will.
 * The serial number stamped on the box and the SIM in it live one join away, on `gps_trackers`,
 * and that join is what this read adds.
 *
 * SAME KEY AND SAME SELECT `TrackerForm` HAS ALWAYS USED, which is the point of moving it here:
 * the sheet's post-save invalidation of `['vehicle-trackers', vehicleId]` now refreshes the area
 * behind it for free, and there is exactly one shape under the key rather than two (which is how
 * `['insurance-details']` ended up returning whichever of its shapes mounted first).
 */

import { supabase } from '@/integrations/supabase/client';

import type { PickableTracker } from './trackerPick';

export const vehicleTrackersKey = (vehicleId: number) => ['vehicle-trackers', vehicleId] as const;

export async function fetchVehicleTrackers(vehicleId: number) {
    const { data, error } = await supabase
        .from('vehicle_trackers')
        .select(`
            id,
            type,
            tracker_id,
            gps_trackers(id, serial_number, sim_number)
        `)
        .eq('vehicle_id', vehicleId);
    if (error) throw error;
    return data || [];
}

// ─── The pool a pairing picks from ───────────────────────────────────────────

/** The key `TrackerSelector` has always used, kept so its post-create invalidation still lands. */
export const availableTrackersKey = (search: string) => ['available-trackers', search] as const;

/** Prefix form — what a pairing clears once a tracker leaves the free pool. */
export const AVAILABLE_TRACKERS_KEY = ['available-trackers'] as const;

/**
 * Every tracker paired to no vehicle.
 *
 * THE FILTER IS CLIENT-SIDE BECAUSE THE ABSENCE OF A ROW IS. PostgREST cannot express "has no
 * `vehicle_trackers` children" as a filter on the parent, so the embed is fetched and the ones
 * carrying children are dropped — which is exactly what the selector this replaces did.
 */
export async function fetchAvailableTrackers(search: string): Promise<PickableTracker[]> {
    let query = supabase
        .from('gps_trackers')
        .select('id, serial_number, sim_number, vehicle_trackers(id)')
        .order('serial_number');
    if (search) query = query.ilike('serial_number', `%${search}%`);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? [])
        .filter((row) => !row.vehicle_trackers || row.vehicle_trackers.length === 0)
        .map((row) => ({
            id: row.id,
            serial_number: row.serial_number == null ? null : String(row.serial_number),
            sim_number: row.sim_number ?? null,
        }));
}

/** The tracker a slot already holds — assigned, therefore never in the free pool. */
export async function fetchTracker(trackerId: number | null): Promise<PickableTracker | null> {
    if (!trackerId) return null;
    const { data, error } = await supabase
        .from('gps_trackers')
        .select('id, serial_number, sim_number')
        .eq('id', trackerId)
        .single();
    if (error) throw error;
    return {
        id: data.id,
        serial_number: data.serial_number == null ? null : String(data.serial_number),
        sim_number: data.sim_number ?? null,
    };
}

/** Record a tracker that is not on the system yet, and hand it back so it can be paired. */
export async function createTracker({
    serial,
    sim,
}: {
    serial: string;
    sim: string;
}): Promise<PickableTracker> {
    const { data, error } = await supabase
        .from('gps_trackers')
        .insert({
            /**
             * `serial_number` IS A NUMERIC COLUMN and the serial is typed as text. The string
             * travels rather than a parsed number — PostgREST hands a numeric literal straight to
             * Postgres, which reads it at full precision, where a JavaScript number has already
             * been through a binary float. The generated types say `number`, so this cast is what
             * says "a numeric literal, not a JS number"; `validateNewTracker` is what keeps a
             * serial Postgres cannot read from ever reaching it.
             */
            serial_number: serial.trim() as unknown as number,
            sim_number: sim.trim() || null,
        })
        .select('id, serial_number, sim_number')
        .single();
    if (error) throw error;
    return {
        id: data.id,
        serial_number: data.serial_number == null ? null : String(data.serial_number),
        sim_number: data.sim_number ?? null,
    };
}

// ─── The pairing itself ──────────────────────────────────────────────────────

/**
 * Point one slot of a vehicle at a tracker, or empty it.
 *
 * `TrackerForm`'S OWN THREE BRANCHES, MOVED: unpair deletes the junction row, repair updates it,
 * and a first pairing inserts one. The sheet is still mounted on `ContractInsights`, so this has
 * to be the one body — two write paths for one junction is how a slot ends up with two rows.
 */
export async function saveVehicleTracker({
    vehicleId,
    type,
    existingPairingId,
    trackerId,
}: {
    vehicleId: number;
    type: 'primary' | 'secondary';
    /** The `vehicle_trackers` row id this slot holds today, or null. */
    existingPairingId: number | null;
    /** The tracker to point it at; null empties the slot. */
    trackerId: number | null;
}): Promise<void> {
    if (existingPairingId != null && trackerId == null) {
        const { error } = await supabase
            .from('vehicle_trackers')
            .delete()
            .eq('id', existingPairingId);
        if (error) throw error;
        return;
    }
    if (existingPairingId != null) {
        const { error } = await supabase
            .from('vehicle_trackers')
            .update({ tracker_id: trackerId as number })
            .eq('id', existingPairingId);
        if (error) throw error;
        return;
    }
    if (trackerId == null) return;

    const { error } = await supabase
        .from('vehicle_trackers')
        .insert({ vehicle_id: vehicleId, tracker_id: trackerId, type });
    if (error) throw error;
}
